import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import { formatPrice } from "../../utils/functions";
import api from "../../lib/api.ts";
import Pagination from "../common/Pagination";
import { useAuth } from "../../context/AuthContext";
import SellerInvoicePaymentPopup from "../common/SellerInvoicePaymentPopup";

const SellerInvoices = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);

  const { user } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchSellerInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          sellerId: user.seller.id,
          filters: {},
        };

        const response = await api.get(`/sellerinvoices/paginated`, { params });

        const data = response?.data?.data || response?.data || [];
        setInvoices(Array.isArray(data) ? data : []);

        // Update pagination state from API response
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.page,
            totalPages: response.data.pagination.totalPages,
            totalItems: response.data.pagination.totalItems,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }
      } catch (err) {
        setError(translate("sellerInvoices.messages.failedToLoad"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerInvoices();
  }, [currentPage, pageSize]);

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };

  const hasPendingPayment = (invoice) => {
    return invoice.payments?.some((p) => p.amount > 0 && p.isApproved === null);
  };

  const hasAnyPayment = (invoice) => {
    return invoice.payments?.some((p) => p.amount > 0);
  };

  const getReceiptUrl = (invoice) => {
    const paymentWithReceipt = invoice.payments?.find((p) => p.imageUrl);
    return paymentWithReceipt?.imageUrl || null;
  };

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusMap = {
      Paid: translate("sellerInvoices.status.paid"),
      Pending: translate("sellerInvoices.status.pending"),
      "Partial Payment": translate("sellerInvoices.status.partialPayment"),
      Cancelled: translate("sellerInvoices.status.cancelled"),
      Refunded: translate("sellerInvoices.status.refunded"),
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  };

  return (
    <div
      className="bg-white rounded-lg shadow border border-gray-200"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div
        className={`px-4 py-3 border-b border-gray-200 flex items-center ${
          isRTL ? "flex-row-reverse" : "justify-between"
        }`}
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          {translate("sellerInvoices.invoice")}
        </h2>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.loading")}
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-600">{error}</div>
      ) : invoices.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.noInvoicesFound")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.vehicle")}
                </th>

                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.totalAmount")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.status")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.date")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.action")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.receipt")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {invoices.map((inv) => {
                // Get vehicle name from sellerInvoiceDetails array
                const vehicleName = inv?.vehicle?.name ?? "-";
                const id = inv?.id ?? "-";
                const date = inv?.createdAt ?? "-";
                const amount = inv?.totalAmount ?? 0;
                const status = inv?.invoicePaymentStatus?.name ?? "-";

                return (
                  <tr key={inv?.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {vehicleName}
                    </td>

                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {formatPrice(amount)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status === "Paid"
                            ? "bg-green-700 text-white"
                            : status === "Pending"
                              ? "bg-yellow-700 text-white"
                              : status === "Partial Payment"
                                ? "bg-blue-700 text-white"
                                : status === "Cancelled"
                                  ? "bg-red-700 text-white"
                                  : status === "Refunded"
                                    ? "bg-purple-700 text-white"
                                    : "bg-gray-700 text-white"
                        }`}
                      >
                        {getStatusText(status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatDate(date)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {/* {(status === "Pending" ||
                        status === "Partial Payment") && ( */}
                      <button
                        disabled={hasAnyPayment(inv)}
                        onClick={() => handlePayNow(inv)}
                        className={`w-full px-3 py-1.5 rounded-md text-sm text-white transition-colors ${
                          hasAnyPayment(inv)
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-orange-500 hover:bg-orange-600"
                        }`}
                      >
                        <span>
                          {translate("sellerInvoices.buttons.payNow")}
                        </span>
                      </button>
                      {/* )} */}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {getReceiptUrl(inv) ? (
                        <button
                          onClick={() =>
                            window.open(getReceiptUrl(inv), "_blank")
                          }
                          className="w-full bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          View 
                        </button>
                      ) : (
                        <div className="w-full text-center text-xs text-gray-500 py-1">
                          No Receipt
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <Pagination
                totalPages={pagination.totalPages}
                currentPage={pagination.currentPage}
                setCurrentPage={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
      {/* Payment Popup */}
      <SellerInvoicePaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType="invoice"
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default SellerInvoices;
